import React, { useState, useMemo } from "react";
import Modal from "../components/Modal";
import SelectBookingStage from "./SelectBookingStage";
import ChargesStage from "./ChargesStage";
import PaymentStage from "./PaymentStage";
// import SuccessStage from "./SuccessStage";
import { getOnsiteItems } from "../data/onsiteItems";
import useSettings from "../../../hooks/useSettings";
import { fmtDateShort, fmtINR } from "../utils/formatters";

const OnsitePaymentFlow = ({
  bookings = [],
  preSelectedBooking,
  onClose,
  onComplete,
}) => {
  const [stage, setStage] = useState(preSelectedBooking ? "charges" : "select");
  const [selectedBooking, setSelectedBooking] = useState(
    preSelectedBooking || null,
  );
  const [lineItems, setLineItems] = useState({});
  const [customLines, setCustomLines] = useState([]);
  const [paymentMode, setPaymentMode] = useState({
    id: "cash",
    label: "Cash",
    icon: "₹",
    typeOfPayment: "offline",
  });
  const [paymentRef, setPaymentRef] = useState("");
  const { setting } = useSettings();
  const onsiteItems = useMemo(
    () => getOnsiteItems(setting?.onsiteChargesInfo),
    [setting?.onsiteChargesInfo],
  );

  const normalizedBooking = selectedBooking
    ? {
        ...selectedBooking,
        ref: selectedBooking.booking_reference || selectedBooking.ref || "N/A",
        room: {
          name:
            selectedBooking.hearing_room ||
            selectedBooking.room?.name ||
            "Unknown Room",
        },
        date: selectedBooking.hearing_date || selectedBooking.date,
        startTime:
          selectedBooking.start_time?.slice(0, 5) ||
          selectedBooking.startTime ||
          "--:--",
        totals: {
          total:
            selectedBooking.total_amount || selectedBooking.totals?.total || 0,
        },
      }
    : null;

  // Derive line items + total from current state

  /*

  const { computedLines, total } = useMemo(() => {
    const lines = [];

    onsiteItems.forEach((item) => {
      const qty = lineItems[item.id];
      if (!qty || qty <= 0) return;
      if (item.id === "tribunalSec") {
        lines.push({
          desc: item.label,
          qty: 1,
          unit: item.unit,
          unitPrice: qty,
          amount: qty,
          custom: true,
        });
      } else {
        lines.push({
          desc: item.label,
          qty,
          unit: item.unit,
          unitPrice: item.price,
          amount: qty * item.price,
        });
      }
    });

    customLines.forEach((cl) => {
      if (cl.desc && cl.amount > 0) {
        lines.push({
          desc: cl.desc,
          qty: 1,
          unit: "",
          unitPrice: cl.amount,
          amount: cl.amount,
          custom: true,
        });
      }
    });

    return {
      computedLines: lines,
      total: lines.reduce((s, l) => s + l.amount, 0),
    };
  }, [lineItems, customLines]);

  */

  const {
    computedLines,
    total: computedTotal,
    gstAmount,
    grandTotal,
  } = useMemo(() => {
    const lines = [];

    onsiteItems.forEach((item) => {
      const qty = lineItems[item.id];
      if (!qty || qty <= 0) return;

      if (item.amountEntry) {
        lines.push({
          desc: item.label,
          qty: 1,
          unit: item.unit || "",
          unitPrice: qty,
          amount: qty,
          hsnCode: item.hsnCode || "",
          custom: true,
        });
      } else {
        lines.push({
          desc: item.label,
          qty,
          unit: item.unit || "",
          unitPrice: item.price || 0,
          amount: qty * (item.price || 0),
          hsnCode: item.hsnCode || "",
        });
      }
    });

    // Custom additional charges
    customLines.forEach((cl) => {
      if (cl.desc && cl.amount > 0) {
        lines.push({
          desc: cl.desc,
          qty: 1,
          unit: "",
          unitPrice: cl.amount,
          amount: cl.amount,
          hsnCode: 999799,
          custom: true,
        });
      }
    });

    const subTotal = lines.reduce((sum, item) => sum + item.amount, 0);

    const gst = Math.round(subTotal * 0.18);
    const totalWithGST = subTotal + gst;

    // API payload format
    const serviceCharge = lines.map((item) => ({
      name: item.desc,
      qty: item.qty,
      price: item.amount,
      hsnCode: item.hsnCode || "",
    }));

    return {
      computedLines: lines,
      total: subTotal,
      gstAmount: gst,
      grandTotal: totalWithGST,
      invoicePayload: {
        booking_id: selectedBooking?.id,
        service_charge: serviceCharge,
        sub_total: subTotal,
        gst_amount: gst,
        total_amount: totalWithGST,
        invoiceType: "onsite",
        TypeOfPayment: paymentMode === "cash" ? "offline" : "online",
        ModeOfPayment: paymentMode || "",
      },
    };
  }, [lineItems, customLines, selectedBooking, paymentMode, onsiteItems]);

  const handleSelectBooking = (b) => {
    setSelectedBooking(b);
    setStage("charges");
  };

  const handleBackToSelect = () => {
    if (preSelectedBooking) {
      onClose();
    } else {
      setStage("select");
    }
  };

  const handleComplete = () => {
    setStage("success");

    if (onComplete) {
      onComplete({
        booking_id: normalizedBooking?.id || selectedBooking?.id,
        service_charge: computedLines.map((item) => ({
          name: item.desc,
          qty: item.qty,
          price: item.amount,
          hsnCode: item.hsnCode,
        })),
        total_amount: grandTotal,
        sub_total: computedTotal,
        gst_amount: gstAmount,
        modeOfPayment: paymentMode?.id,
        typeOfPayment: paymentMode?.typeOfPayment,
      });
    }
  };

  if (stage === "select") {
    return (
      <Modal
        open
        onClose={onClose}
        title="Record On-Site Payment"
        subtitle="Select the booking for which charges were incurred"
        width={680}
        key="select"
      >
        <SelectBookingStage
          bookings={bookings}
          onSelect={handleSelectBooking}
          onClose={onClose}
        />
      </Modal>
    );
  }

  if (stage === "charges") {
    return (
      <Modal
        open
        onClose={onClose}
        title={`Record On-Site Payment · ${normalizedBooking?.ref || selectedBooking?.booking_reference}`}
        subtitle={`${normalizedBooking?.room?.name || selectedBooking?.hearing_room} · ${fmtDateShort(normalizedBooking?.date || selectedBooking?.hearing_date)} · ${normalizedBooking?.startTime || selectedBooking?.start_time?.slice(0, 5)}`}
        width={780}
        key="charges"
      >
        <ChargesStage
          onsiteItems={onsiteItems}
          lineItems={lineItems}
          customLines={customLines}
          setLineItems={setLineItems}
          setCustomLines={setCustomLines}
          computedTotal={computedTotal}
          computedLines={computedLines}
          onBack={handleBackToSelect}
          onNext={() => setStage("payment")}
        />
      </Modal>
    );
  }

  if (stage === "payment") {
    return (
      <Modal
        open
        onClose={onClose}
        title="Collect Payment"
        subtitle={`${normalizedBooking?.ref || selectedBooking?.booking_reference} · ${fmtINR(computedTotal)} due`}
        width={650}
        key="payment"
      >
        <PaymentStage
          selectedBooking={normalizedBooking || selectedBooking}
          paymentMode={paymentMode}
          setPaymentMode={setPaymentMode}
          paymentRef={paymentRef}
          setPaymentRef={setPaymentRef}
          computedLines={computedLines}
          total={computedTotal}
          onBack={() => setStage("charges")}
          onComplete={handleComplete}
          subtotal={computedTotal}
          gstAmount={gstAmount}
          grandTotal={grandTotal}
        />
      </Modal>
    );
  }

  // if (stage === "success") {
  //   return (
  //     <Modal open onClose={onClose} width={480}>
  //       <SuccessStage
  //         selectedBooking={normalizedBooking || selectedBooking}
  //         paymentMode={paymentMode}
  //         total={computedTotal}
  //         invoiceNumber={invoiceNumber}
  //         onClose={onClose}
  //       />
  //     </Modal>
  //   );
  // }

  return null;
};

export default OnsitePaymentFlow;
